import { CommonModule } from '@angular/common';
import { Component, OnDestroy } from '@angular/core';
import { ReactiveFormsModule, FormControl, FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';

// Components
import { AlertComponent } from '../../../../../../shared/_components/alert/alert.component';
import { ButtonComponent } from '../../../../../../shared/_components/button/button.component';
import { TextAreaComponent } from '../../../../../../shared/_components/text-area/text-area.component';
import { FlowMediaUploadStep } from '../../flows/flow-media-upload/step/flow-media-upload-step.component';

// Shared
import { GetAssetStatusResponse } from '@oddr/user-services';
import { Statuses } from '@oddr/user-schemas';

// Models
import { TranscriptionResponse } from '../../../models/media-upload.model';

@Component({
  selector: 'pp-step-media-video-transcription',
  templateUrl: './step-media-video-transcription.component.html',
  styleUrl: './step-media-video-transcription.component.scss',
  imports: [CommonModule, ReactiveFormsModule, AlertComponent, ButtonComponent, TextAreaComponent],
})
export class StepMediaVideoTranscriptionComponent extends FlowMediaUploadStep implements OnDestroy {

  // Component handler (!IMPORTANT - Must match step identifier for this flow)
  identifier = 'mediaUploadFlowStepVideoTranscription';

  // Aliases
  statuses = Statuses;

  // Form handlers
  pollingStatusUpdate?: GetAssetStatusResponse;
  transcriptionEditError?: string | null;
  transcriptionEditMode = false;
  transcriptionEdited = false;
  transcriptionReceived = false;

  // Subscriptions
  private readonly processingPollingStatus$?: Subscription;

  constructor() {
    super();

    // Define form
    this.form = new FormGroup({
      muTranscription: new FormControl<string>(''),
    });

    // Listen for status polling
    this.processingPollingStatus$ = this.mediaUploadService.processingPollingStatus$.subscribe({
      next: (response: GetAssetStatusResponse) => {
        this.pollingStatusUpdate = response;
        this.cdr.markForCheck();

        // If the transcription is complete, retrieve the transaction and inject it into the form.
        if (this.pollingStatusUpdate.transcriptionStatus === this.statuses.Complete) {
          this.mediaUploadService.getTranscription().subscribe({
            next: (response: TranscriptionResponse) => {
              this.form.get('muTranscription')?.setValue(response.text, { emitEvent: false });
              this.transcriptionReceived = true;
              this.cdr.markForCheck();
            },
          });
        }
      },
    });

    // Subscribe to changes in the transcription text to mark as edited
    this.form.get('muTranscription')?.valueChanges.subscribe({
      next: () => this.transcriptionEdited = true,
    });
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.processingPollingStatus$?.unsubscribe();
  }

  /**
   * The received transcription text of the video. If no transcription is available, returns an empty string.
   * @type {string}
   * @memberof StepMediaVideoTranscriptionComponent
   */
  get transcriptionText(): string {
    return this.form.get('muTranscription')?.value ?? '';
  }

  /**
   * Submits the edited transcription and navigates to the next step.
   * @memberof StepMediaVideoTranscriptionComponent
   */
  submitAndGoToNextStep(): void {
    if (this.transcriptionEdited) {
      // Post the form data
      this.mediaUploadService.postEditedTranscription(this.form.get('muTranscription')?.value).subscribe({
        next: () => this.goToNextStep(),
      });
    }
    else this.goToNextStep();
  }

  /**
   * Toggles the status of edit mode.
   * @memberof StepMediaVideoTranscriptionComponent
   */
  toggleEditMode(): void {
    this.transcriptionEditMode = !this.transcriptionEditMode;
  }
}
